import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Video } from 'lucide-react';
import { format } from 'date-fns';
import { da, enUS } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import {
  usePatientAppointments,
  type PatientAppointmentItem,
} from '@/hooks/usePatientAppointments';
import { useJoinWherebyMeeting } from '@/hooks/useWhereby';
import { isVideoAppointment, openExternalMeeting } from '@/lib/whereby';
import { AppointmentModal } from '@/pages/appointments/AppointmentModal';
import { AppointmentDetailsModal } from '@/pages/appointments/AppointmentDetailsModal';
import { Button } from '@/components/ui/Button';
import { ListRow } from '@/components/ui/Card';
import { DateBlock } from '@/components/ui/DateBlock';
import { StatusChip, type StatusTone } from '@/components/ui/Chip';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  appointmentIsOnClinicDay,
  appointmentStatusLabelKey,
  resolveAppointmentStatus,
  type AppointmentLifecycleStatus,
} from '@/utils/appointmentStatus';

interface VideoMeetingsTabProps {
  patientId: string;
  clinicId: string;
}

function videoStatusTone(status: AppointmentLifecycleStatus): StatusTone {
  switch (status) {
    case 'upcoming':
      return 'upcoming';
    case 'checked_in':
    case 'under_treatment':
      return 'draft';
    case 'treatment_finished':
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'no_show':
      return 'no_show';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function VideoMeetingsTab({ patientId, clinicId }: VideoMeetingsTabProps) {
  const { t, i18n } = useTranslation();
  const { data: appointments, isLoading, error, refetch } = usePatientAppointments(patientId);
  const join = useJoinWherebyMeeting(clinicId);
  const [showSchedule, setShowSchedule] = useState(false);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const dateLocale = i18n.language === 'da' ? da : enUS;
  const now = new Date();

  const videoAppointments = useMemo(
    () => (appointments ?? []).filter((apt) => isVideoAppointment(apt)),
    [appointments],
  );
  const upcoming = videoAppointments.filter((apt) => {
    const status = resolveAppointmentStatus({
      status: apt.status,
      visitStatus: apt.visit_status,
      startsAt: apt.starts_at,
      endsAt: apt.ends_at,
      now,
    });
    return status === 'upcoming' || status === 'checked_in' || status === 'under_treatment';
  });
  const past = videoAppointments.filter((apt) => !upcoming.includes(apt));

  const handleJoin = async (appointmentId: string) => {
    try {
      const result = await join.mutateAsync(appointmentId);
      if (result.hostUrl) openExternalMeeting(result.hostUrl);
    } catch {
      toast.error(t('patients.video_meetings.join_error'));
    }
  };

  const renderRow = (apt: PatientAppointmentItem) => {
    const start = new Date(apt.starts_at);
    const end = new Date(apt.ends_at);
    const lifecycle = resolveAppointmentStatus({
      status: apt.status,
      visitStatus: apt.visit_status,
      startsAt: apt.starts_at,
      endsAt: apt.ends_at,
      now,
    });
    const canJoin = apt.status !== 'cancelled' && apt.status !== 'no_show';
    return (
      <ListRow
        key={apt.id}
        role="button"
        tabIndex={0}
        onClick={() => setDetailsId(apt.id)}
        className="w-full cursor-pointer text-left hover:bg-ds-sunk"
      >
        <DateBlock
          weekday={format(start, 'EEE', { locale: dateLocale })}
          day={format(start, 'd')}
          month={format(start, 'MMM', { locale: dateLocale })}
          today={appointmentIsOnClinicDay(apt.starts_at, now)}
        />
        <div className="min-w-0 flex-1">
          <p className={cn(TYPE.body, 'font-semibold text-ds-text')}>
            {apt.service_name ?? t('patients.video_meetings.untitled')}
          </p>
          <p className={cn(TYPE.small, 'mt-s1 tabular-nums text-ds-muted')}>
            {format(start, 'HH:mm')}–{format(end, 'HH:mm')}
            {apt.clinician_name ? ` · ${apt.clinician_name}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-s2">
          <StatusChip tone={videoStatusTone(lifecycle)}>
            {t(appointmentStatusLabelKey(lifecycle))}
          </StatusChip>
          {canJoin ? (
            <Button
              type="button"
              variant="secondary"
              onClick={(event) => {
                event.stopPropagation();
                void handleJoin(apt.id);
              }}
              loading={join.isPending}
            >
              <Video className="h-4 w-4" aria-hidden />
              {t('patients.video_meetings.join')}
            </Button>
          ) : null}
        </div>
      </ListRow>
    );
  };

  return (
    <div className="flex flex-col gap-s4">
      <PatientTabFrame
        title={t('patients.tabs.video_meetings')}
        count={videoAppointments.length}
        action={
          <Button type="button" onClick={() => setShowSchedule(true)}>
            {t('patients.video_meetings.schedule')}
          </Button>
        }
        loading={isLoading}
        error={error ? t('patients.video_meetings.load_error') : undefined}
        onRetry={() => void refetch()}
        empty={
          !isLoading && !error && videoAppointments.length === 0
            ? {
                icon: <Video className="h-7 w-7" />,
                title: t('patients.video_meetings.empty'),
                description: t('patients.video_meetings.empty_hint'),
              }
            : undefined
        }
      >
        {upcoming.length > 0 ? (
          <section className="mt-s4">
            <h3 className={cn(TYPE.label, 'px-s4 py-s2 text-ds-muted')}>
              {t('patients.video_meetings.upcoming')}
            </h3>
            {upcoming.map(renderRow)}
          </section>
        ) : null}
        {past.length > 0 ? (
          <section className="mt-s4">
            <h3 className={cn(TYPE.label, 'px-s4 py-s2 text-ds-muted')}>
              {t('patients.video_meetings.past')}
            </h3>
            {past.map(renderRow)}
          </section>
        ) : null}
      </PatientTabFrame>

      {showSchedule ? (
        <AppointmentModal
          clinicId={clinicId}
          mode="create"
          initialPatientId={patientId}
          initialVideoMeeting
          onClose={() => setShowSchedule(false)}
        />
      ) : null}
      {detailsId ? (
        <AppointmentDetailsModal
          appointmentId={detailsId}
          clinicId={clinicId}
          onClose={() => setDetailsId(null)}
        />
      ) : null}
    </div>
  );
}
